/**
 * Homepage State Hook - Composition hook
 *
 * Combines useHomepageData with data transformations.
 * This is the main hook consumed by HomepageProvider.
 */
import { useHomepageData } from './useHomepageData'

import type { UseHomepageDataOptions } from './useHomepageData'
import type { HomepageBanner, HomepageCategoryCard, HomepageProductSlider, HomepagePromoBlock } from '../../types'

export type UseHomepageStateOptions = UseHomepageDataOptions

export interface UseHomepageStateResult {
  /** Raw config data */
  config: {
    banners: HomepageBanner[]
    product_sliders: HomepageProductSlider[]
    promo_blocks: HomepagePromoBlock[]
    category_cards: HomepageCategoryCard[]
  } | null
  /** Filtered banners (null items removed) */
  banners: HomepageBanner[]
  /** Filtered product sliders (null items removed) */
  productSliders: HomepageProductSlider[]
  /** Filtered promo blocks (null items removed) */
  promoBlocks: HomepagePromoBlock[]
  /** Filtered category cards (null items removed) */
  categoryCards: HomepageCategoryCard[]
  /** Loading state */
  isLoading: boolean
  /** Normalized error (Error | null) */
  error: Error | null
}

/**
 * Composition hook for homepage state
 *
 * Combines data fetching with filtering and error normalization.
 * This is the preferred hook for components to consume.
 *
 * @example
 * const { banners, productSliders, isLoading, error } = useHomepageState()
 */
export const useHomepageState = (options: UseHomepageStateOptions = {}): UseHomepageStateResult => {
  const query = useHomepageData(options)

  // Note: request() returns the GraphQL response body directly ({ data: { homepageConfig: ... } })
  // and response.data in apiClient returns { homepageConfig: ... }
  // So query.data is { homepageConfig: ... } - no extra .data level needed
  const config = query.data?.homepageConfig ?? null

  // Filter out null items that may come from GraphQL
  const banners = (config?.banners ?? []).filter(
    (item): item is NonNullable<typeof item> => item !== null
  )
  const productSliders = (config?.product_sliders ?? [])
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .map((slider) => ({
      ...slider,
      display_type: slider.display_type ?? 'slider',
    }))
  const promoBlocks = (config?.promo_blocks ?? []).filter(
    (item): item is NonNullable<typeof item> => item !== null
  )
  const categoryCards = (config?.category_cards ?? []).filter(
    (item): item is NonNullable<typeof item> => item !== null
  )

  // Normalize error to Error | null (TanStack Query v5 returns unknown)
  const error = query.error instanceof Error
    ? query.error
    : query.error
      ? new Error(String(query.error))
      : null

  return {
    config,
    banners,
    productSliders,
    promoBlocks,
    categoryCards,
    isLoading: query.isLoading,
    error,
  }
}
